/**
 * Focus keeps the attempt (D70, D75): its state API, at the same base as its statements, keyed by
 * the launch's registration — `<endpoint>activities/state?registration=<uuid>`, one document per
 * attempt. Reached through the player's server (`/api/v1/launch-state`), which checks the host
 * against `PLAYER_ALLOWED_HOSTS` and keeps nothing; the token travels in the request body only.
 *
 * What Focus answered goes to the browser console as well, so a tester inside Focus — where the
 * events drawer is not shown — can see whether the attempt was kept.
 */

import type { LaunchState } from '../types.js';

interface Answer {
  readonly ok?: boolean;
  readonly found?: boolean;
  readonly document?: unknown;
  readonly status?: number;
  readonly error?: string;
  readonly refused?: string;
}

export function focusState(lrs: { readonly endpoint: string; readonly auth: string | null }, registration: string): LaunchState {
  const call = async (op: 'get' | 'put', extra: { document?: unknown } = {}, keepalive = false): Promise<Answer> => {
    const response = await fetch('/api/v1/launch-state', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ op, launch: lrs, registration, ...extra }),
      keepalive,
    });
    if (!response.ok) return { ok: false, status: response.status, error: `the player's server answered ${response.status}` };
    return (await response.json()) as Answer;
  };

  return {
    async load() {
      const answer = await call('get');
      if (answer.ok !== true) {
        const why = answer.refused ?? answer.error ?? 'no answer';
        console.warn(`[assets player] Focus state: not read - ${why}`);
        throw new Error(why);
      }
      console.info(`[assets player] Focus state: ${answer.found === true ? 'a kept attempt was found' : 'nothing kept yet'}`);
      return answer.found === true ? answer.document : null;
    },
    async save(document, options) {
      try {
        const answer = await call('put', { document }, options?.keepalive === true);
        if (answer.ok === true) console.debug(`[assets player] Focus state: saved (${answer.status ?? 200})`);
        else console.warn(`[assets player] Focus state: not saved - ${answer.refused ?? answer.error ?? 'no answer'}`);
        return answer.ok === true;
      } catch (error) {
        console.warn(`[assets player] Focus state: not saved - ${error instanceof Error ? error.message : String(error)}`);
        return false;
      }
    },
  };
}
